import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  title?: string;
  duration?: number;
  key?: string;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (type: ToastType, message: string, title?: string, duration?: number) => void;
  removeToast: (id: string) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  warning: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Store timeout refs for cleanup
  const timeoutRefs = useRef<Set<NodeJS.Timeout>>(new Set());

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all pending timeouts when provider unmounts
      timeoutRefs.current.forEach(timeoutId => clearTimeout(timeoutId));
      timeoutRefs.current.clear();
    };
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const MAX_CONCURRENT_TOASTS = 4;

  const showToast = useCallback(
    (type: ToastType, message: string, title?: string, duration: number = 5000, key?: string) => {
      // Deduplicate toasts by provided key if present, otherwise by type+title+message
      setToasts((prev) => {
        const duplicate = prev.find((t) => {
          if (key && t.key) return t.key === key;
          return t.type === type && t.title === title && t.message === message;
        });
        if (duplicate) return prev; // already shown

        const id = Math.random().toString(36).substring(2, 9);
        const newToast: Toast = { id, type, message, title, duration, key };

        // Limit concurrent toasts to avoid flooding UI
        const next = [...prev, newToast];
        if (next.length > MAX_CONCURRENT_TOASTS) {
          // remove oldest
          next.splice(0, next.length - MAX_CONCURRENT_TOASTS);
        }

        // Schedule auto-remove with cleanup tracking
        if (duration > 0) {
          const timeoutId = setTimeout(() => {
            removeToast(id);
            timeoutRefs.current.delete(timeoutId);
          }, duration);
          timeoutRefs.current.add(timeoutId);
        }

        return next;
      });
    },
    [removeToast]
  );

  const success = useCallback(
    (message: string, title?: string) => showToast('success', message, title),
    [showToast]
  );

  const error = useCallback(
    (message: string, title?: string) => showToast('error', message, title),
    [showToast]
  );

  const warning = useCallback(
    (message: string, title?: string) => showToast('warning', message, title),
    [showToast]
  );

  const info = useCallback(
    (message: string, title?: string) => showToast('info', message, title),
    [showToast]
  );

  const value = useMemo(() => ({
    toasts,
    showToast,
    removeToast,
    success,
    error,
    warning,
    info,
  }), [toasts, showToast, removeToast, success, error, warning, info]);

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};
